import type { CurrencyAmount } from '@/features/expense/types/expense.types';

/**
 * Format a single amount in its own currency. Never converts — the amount
 * and currency must already be a matching pair (e.g. from one expense, or
 * one bucket of a currency-grouped total).
 */
export function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

/**
 * Join a currency-grouped total (e.g. from ExpenseStatistics.totalsByCurrency)
 * into a single display string — "$1,433.81 · ₹567.00 · AED 817.29" — instead
 * of summing mismatched currencies into one fabricated number. Amounts with a
 * 0 total are dropped so the string doesn't fill up with "$0.00" noise.
 */
export function formatMoneyList(amounts: CurrencyAmount[] | undefined | null): string {
  if (!amounts || amounts.length === 0) return formatMoney(0, 'USD');
  const nonZero = amounts.filter((a) => a.totalAmount !== 0);
  const list = nonZero.length > 0 ? nonZero : amounts.slice(0, 1);
  return list.map((a) => formatMoney(a.totalAmount, a.currency)).join(' · ');
}

/** Pulls one currency's amount out of a currency-grouped total — 0 if that currency isn't present. */
export function pickAmount(amounts: CurrencyAmount[] | undefined | null, currency: string): number {
  return amounts?.find((a) => a.currency === currency)?.totalAmount ?? 0;
}

/** `pickAmount`, pre-formatted for display. */
export function formatMoneyFor(amounts: CurrencyAmount[] | undefined | null, currency: string): string {
  return formatMoney(pickAmount(amounts, currency), currency);
}
